import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Book, SummaryOption } from '../../types';
import {
  getGraphQLClient,
  UPDATE_BOOK,
  GENERATE_SUMMARY_OPTIONS,
  GENERATE_RANDOM_TITLE,
  GENERATE_SUBTITLE,
} from '../../lib/graphql';
import { Button, AiEnhancedInput, AiEnhancedTextarea } from '../common';
import type { AiGenerateAction } from '../common';
import { captureException } from '../../lib/sentry';
import { useAuth } from '../../hooks/useAuth';

interface DescriptionStepProps {
  book: Book;
  onBookChange: (book: Book) => void;
  onNext: () => void;
  onBack: () => void;
  isEditMode?: boolean;
}

export default function DescriptionStep({
  book,
  onBookChange,
  onNext,
  onBack,
  isEditMode = false,
}: DescriptionStepProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const topRef = useRef<HTMLDivElement>(null);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);
  const [title, setTitle] = useState(book.title || '');
  const [subtitle, setSubtitle] = useState(book.subtitle || '');
  const [summary, setSummary] = useState(book.summary || '');
  const [error, setError] = useState('');

  // Title suggestions state
  const [cachedTitles, setCachedTitles] = useState<string[]>([]);
  const [displayedTitles, setDisplayedTitles] = useState<string[]>([]);
  const [showTitleSuggestions, setShowTitleSuggestions] = useState(false);
  const [titlesBasedOnSummary, setTitlesBasedOnSummary] = useState(false);

  // Summary suggestions state
  const [cachedSummaries, setCachedSummaries] = useState<SummaryOption[]>([]);
  const [displayedSummaries, setDisplayedSummaries] = useState<SummaryOption[]>([]);
  const [showSummarySuggestions, setShowSummarySuggestions] = useState(false);
  const [expandedSummaryId, setExpandedSummaryId] = useState<string | null>(null);

  // Subtitle suggestions state
  const [isGeneratingSubtitle, setIsGeneratingSubtitle] = useState(false);
  const [cachedSubtitles, setCachedSubtitles] = useState<string[]>([]);
  const [displayedSubtitles, setDisplayedSubtitles] = useState<string[]>([]);
  const [showSubtitleSuggestions, setShowSubtitleSuggestions] = useState(false);

  // Edit mode state (tracks unsaved changes for potential future use)
  const [, setHasUnsavedChanges] = useState(false);

  // Auto scroll to top when component mounts
  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Sync state with book prop when it changes (e.g., when navigating back)
  // Split into separate effects so changes to one field don't reset others
  useEffect(() => {
    setTitle(book.title || '');
  }, [book.id, book.title]);

  useEffect(() => {
    setSubtitle(book.subtitle || '');
  }, [book.id, book.subtitle]);

  useEffect(() => {
    setSummary(book.summary || '');
  }, [book.id, book.summary]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    if (isEditMode) setHasUnsavedChanges(true);
  };

  const handleTitleBlur = async () => {
    // In edit mode, don't auto-save - wait for explicit Save button
    if (isEditMode) return;

    if (title !== book.title) {
      try {
        const client = getGraphQLClient();
        const data = await client.request<{
          updateBook: { book: Book; errors: string[] };
        }>(UPDATE_BOOK, {
          id: book.id,
          title: title || null,
        });

        if (data.updateBook.book) {
          onBookChange({ ...book, title: data.updateBook.book.title });
        }
      } catch (err) {
        console.error('Failed to update title:', err);
      }
    }
  };

  const handleSubtitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSubtitle(e.target.value);
    if (isEditMode) setHasUnsavedChanges(true);
  };

  const handleSubtitleBlur = async () => {
    // In edit mode, don't auto-save - wait for explicit Save button
    if (isEditMode) return;

    if (subtitle !== book.subtitle) {
      try {
        const client = getGraphQLClient();
        const data = await client.request<{
          updateBook: { book: Book; errors: string[] };
        }>(UPDATE_BOOK, {
          id: book.id,
          subtitle: subtitle || null,
        });

        if (data.updateBook.book) {
          onBookChange({ ...book, subtitle: data.updateBook.book.subtitle });
        }
      } catch (err) {
        console.error('Failed to update subtitle:', err);
      }
    }
  };

  const fetchSubtitles = async (): Promise<string[]> => {
    const client = getGraphQLClient();
    const data = await client.request<{
      generateSubtitle: { subtitles: string[]; errors: string[] };
    }>(GENERATE_SUBTITLE, {
      title: title.trim() || book.title || '',
      themes: book.themes,
      language: book.language,
      count: 20,
      summary: summary.trim() || null,
    });

    if (data.generateSubtitle.errors.length > 0) {
      setError(data.generateSubtitle.errors[0]);
      return [];
    }

    return data.generateSubtitle.subtitles || [];
  };

  const handleGenerateSubtitle = async () => {
    if (!title.trim() && !book.title) {
      setError(t('wizard.enterTitleFirst'));
      return;
    }

    setIsGeneratingSubtitle(true);
    setError('');

    try {
      if (cachedSubtitles.length >= 3) {
        const nextThree = cachedSubtitles.slice(0, 3);
        setCachedSubtitles(cachedSubtitles.slice(3));
        setDisplayedSubtitles(nextThree);
        setShowSubtitleSuggestions(true);
      } else {
        const newSubtitles = await fetchSubtitles();
        if (newSubtitles.length > 0) {
          const nextThree = newSubtitles.slice(0, 3);
          setCachedSubtitles(newSubtitles.slice(3));
          setDisplayedSubtitles(nextThree);
          setShowSubtitleSuggestions(true);
        }
      }
    } catch (err) {
      captureException(err, {
        component: 'DescriptionStep',
        action: 'generateSubtitle',
      });
      setError(t('common.error'));
    } finally {
      setIsGeneratingSubtitle(false);
    }
  };

  const handleRerollSubtitles = async () => {
    setIsGeneratingSubtitle(true);
    setError('');

    try {
      if (cachedSubtitles.length >= 3) {
        const nextThree = cachedSubtitles.slice(0, 3);
        setCachedSubtitles(cachedSubtitles.slice(3));
        setDisplayedSubtitles(nextThree);
      } else {
        const newSubtitles = await fetchSubtitles();
        if (newSubtitles.length > 0) {
          const nextThree = newSubtitles.slice(0, 3);
          setCachedSubtitles(newSubtitles.slice(3));
          setDisplayedSubtitles(nextThree);
        }
      }
    } catch (err) {
      captureException(err, {
        component: 'DescriptionStep',
        action: 'rerollSubtitles',
      });
      setError(t('common.error'));
    } finally {
      setIsGeneratingSubtitle(false);
    }
  };

  const handleSelectSubtitle = async (selectedSubtitle: string) => {
    setSubtitle(selectedSubtitle);
    setShowSubtitleSuggestions(false);

    // In edit mode, mark as unsaved instead of auto-saving
    if (isEditMode) {
      setHasUnsavedChanges(true);
      return;
    }

    // Auto-save the selected subtitle
    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        updateBook: { book: Book; errors: string[] };
      }>(UPDATE_BOOK, {
        id: book.id,
        subtitle: selectedSubtitle,
      });

      if (data.updateBook.book) {
        onBookChange({ ...book, subtitle: selectedSubtitle });
      }
    } catch (err) {
      console.error('Failed to save subtitle:', err);
    }
  };

  const handleCloseSubtitleSuggestions = () => {
    setShowSubtitleSuggestions(false);
  };

  const handleSummaryChange = (newSummary: string) => {
    setSummary(newSummary);
    // Clear cached titles and summaries when summary changes - they need to be regenerated with new context
    setCachedTitles([]);
    setCachedSummaries([]);
    if (isEditMode) setHasUnsavedChanges(true);
  };

  const handleSummaryBlur = async () => {
    // In edit mode, don't auto-save - wait for explicit Save button
    if (isEditMode) return;

    if (summary !== book.summary) {
      try {
        const client = getGraphQLClient();
        const data = await client.request<{
          updateBook: { book: Book; errors: string[] };
        }>(UPDATE_BOOK, {
          id: book.id,
          summary: summary || null,
        });

        if (data.updateBook.book) {
          onBookChange({ ...book, summary });
        }
      } catch (err) {
        console.error('Failed to update summary:', err);
      }
    }
  };

  const handleNext = async () => {
    // Validate required fields
    const trimmedTitle = title.trim();
    const trimmedSummary = summary.trim();
    const trimmedSubtitle = subtitle.trim() || undefined;

    if (!trimmedTitle || !trimmedSummary) {
      setError(t('wizard.requiredFieldsMissing'));
      return;
    }

    // Save title, subtitle, and summary before proceeding
    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        updateBook: { book: Book; errors: string[] };
      }>(UPDATE_BOOK, {
        id: book.id,
        title: trimmedTitle,
        subtitle: trimmedSubtitle,
        summary: trimmedSummary,
      });

      if (data.updateBook.errors.length > 0) {
        setError(data.updateBook.errors[0]);
        return;
      }

      if (data.updateBook.book) {
        onBookChange({
          ...book,
          title: trimmedTitle,
          subtitle: trimmedSubtitle,
          summary: trimmedSummary,
        });
      }
    } catch (err) {
      captureException(err, {
        component: 'DescriptionStep',
        action: 'saveBeforeNext',
        bookId: book.id,
      });
      setError(t('common.error'));
      return;
    }

    onNext();
  };

  const fetchTitles = async (): Promise<string[]> => {
    const client = getGraphQLClient();
    const summaryForTitles = summary.trim() || null;
    const currentTitleText = title.trim() || null;
    const data = await client.request<{
      generateRandomTitle: { titles: string[]; errors: string[] };
    }>(GENERATE_RANDOM_TITLE, {
      bookType: book.bookType.toUpperCase(),
      themes: book.themes,
      language: book.language,
      count: 30,
      summary: summaryForTitles,
      currentTitle: currentTitleText,
      readerAgeFrom: book.readerAgeFrom,
      readerAgeTo: book.readerAgeTo,
    });

    if (data.generateRandomTitle.errors.length > 0) {
      setError(data.generateRandomTitle.errors[0]);
      return [];
    }

    setTitlesBasedOnSummary(!!summaryForTitles);
    return data.generateRandomTitle.titles || [];
  };

  const handleGenerateTitle = async () => {
    setIsGeneratingTitle(true);
    setError('');

    try {
      // Clear cache and fetch fresh titles when user clicks "Generate with AI" / "Enhance with AI"
      setCachedTitles([]);
      const newTitles = await fetchTitles();
      if (newTitles.length > 0) {
        const nextThree = newTitles.slice(0, 3);
        setCachedTitles(newTitles.slice(3));
        setDisplayedTitles(nextThree);
        setShowTitleSuggestions(true);
      }
    } catch (err) {
      captureException(err, {
        component: 'DescriptionStep',
        action: 'generateTitle',
      });
      setError(t('common.error'));
    } finally {
      setIsGeneratingTitle(false);
    }
  };

  const handleRerollTitles = async () => {
    setIsGeneratingTitle(true);
    setError('');

    try {
      if (cachedTitles.length >= 3) {
        // Use cached titles
        const nextThree = cachedTitles.slice(0, 3);
        setCachedTitles(cachedTitles.slice(3));
        setDisplayedTitles(nextThree);
      } else {
        // Fetch more titles
        const newTitles = await fetchTitles();
        if (newTitles.length > 0) {
          const nextThree = newTitles.slice(0, 3);
          setCachedTitles(newTitles.slice(3));
          setDisplayedTitles(nextThree);
        }
      }
    } catch (err) {
      captureException(err, {
        component: 'DescriptionStep',
        action: 'rerollTitles',
      });
      setError(t('common.error'));
    } finally {
      setIsGeneratingTitle(false);
    }
  };

  const handleSelectTitle = async (selectedTitle: string) => {
    setTitle(selectedTitle);
    setShowTitleSuggestions(false);

    // In edit mode, mark as unsaved instead of auto-saving
    if (isEditMode) {
      setHasUnsavedChanges(true);
      // For non-fiction, auto-generate subtitle if empty
      if (book.bookType?.toLowerCase() === 'non_fiction' && !subtitle.trim()) {
        triggerSubtitleGeneration(selectedTitle);
      }
      return;
    }

    // Auto-save the selected title
    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        updateBook: { book: Book; errors: string[] };
      }>(UPDATE_BOOK, {
        id: book.id,
        title: selectedTitle,
      });

      if (data.updateBook.book) {
        onBookChange({ ...book, title: selectedTitle });
      }

      // For non-fiction, auto-generate subtitle if empty
      if (book.bookType?.toLowerCase() === 'non_fiction' && !subtitle.trim()) {
        triggerSubtitleGeneration(selectedTitle);
      }
    } catch (err) {
      console.error('Failed to save title:', err);
    }
  };

  // Helper function to trigger subtitle generation with a specific title
  const triggerSubtitleGeneration = async (titleForSubtitle: string) => {
    setIsGeneratingSubtitle(true);
    setError('');

    try {
      // Clear cached subtitles since title changed
      setCachedSubtitles([]);

      const client = getGraphQLClient();
      const data = await client.request<{
        generateSubtitle: { subtitles: string[]; errors: string[] };
      }>(GENERATE_SUBTITLE, {
        title: titleForSubtitle,
        themes: book.themes,
        language: book.language,
        count: 20,
        summary: summary.trim() || null,
      });

      if (data.generateSubtitle.errors.length > 0) {
        setError(data.generateSubtitle.errors[0]);
        return;
      }

      const newSubtitles = data.generateSubtitle.subtitles || [];
      if (newSubtitles.length > 0) {
        const nextThree = newSubtitles.slice(0, 3);
        setCachedSubtitles(newSubtitles.slice(3));
        setDisplayedSubtitles(nextThree);
        setShowSubtitleSuggestions(true);
      }
    } catch (err) {
      captureException(err, {
        component: 'DescriptionStep',
        action: 'triggerSubtitleGeneration',
      });
      setError(t('common.error'));
    } finally {
      setIsGeneratingSubtitle(false);
    }
  };

  const handleCloseTitleSuggestions = () => {
    setShowTitleSuggestions(false);
  };

  // AI dropdown action handlers
  const handleTitleAiAction = (action: AiGenerateAction, _instructions?: string) => {
    // All generate/rephrase actions trigger title generation
    // Instructions support could be added to the backend in the future
    if (action === 'generate' || action === 'generateWithInstructions' || action === 'rephrase' || action === 'rephraseWithInstructions') {
      handleGenerateTitle();
    }
    // 'openInChat' is not supported for title field
  };

  const handleSubtitleAiAction = (action: AiGenerateAction, _instructions?: string) => {
    // All generate/rephrase actions trigger subtitle generation
    if (action === 'generate' || action === 'generateWithInstructions' || action === 'rephrase' || action === 'rephraseWithInstructions') {
      handleGenerateSubtitle();
    }
  };

  const handleSummaryAiAction = (action: AiGenerateAction, _instructions?: string) => {
    // All generate/rephrase actions trigger summary generation
    if (action === 'generate' || action === 'generateWithInstructions' || action === 'rephrase' || action === 'rephraseWithInstructions') {
      handleGenerateSummary();
    }
  };

  const fetchSummaryOptions = async (): Promise<SummaryOption[]> => {
    const client = getGraphQLClient();
    // Use current (potentially unsaved) title and summary from local state
    const currentTitle = title.trim() || book.title;
    const currentSummary = summary.trim() || null;

    const data = await client.request<{
      generateSummaryOptions: { summaries: SummaryOption[]; errors: string[] };
    }>(GENERATE_SUMMARY_OPTIONS, {
      bookType: book.bookType.toUpperCase(),
      themes: book.themes,
      title: currentTitle,
      language: book.language || 'en',
      summary: currentSummary,
      readerAgeFrom: book.readerAgeFrom,
      readerAgeTo: book.readerAgeTo,
    });

    if (data.generateSummaryOptions.errors.length > 0) {
      setError(data.generateSummaryOptions.errors[0]);
      return [];
    }

    return data.generateSummaryOptions.summaries || [];
  };

  const handleGenerateSummary = async () => {
    setIsGeneratingSummary(true);
    setError('');

    try {
      // Clear cache and fetch fresh summaries when user clicks "Generate with AI" / "Enhance with AI"
      // This ensures we always use the current summary text for enhancement
      setCachedSummaries([]);
      setExpandedSummaryId(null);
      const newSummaries = await fetchSummaryOptions();
      if (newSummaries.length > 0) {
        const nextThree = newSummaries.slice(0, 3);
        setCachedSummaries(newSummaries.slice(3));
        setDisplayedSummaries(nextThree);
        setShowSummarySuggestions(true);
      }
    } catch (err) {
      captureException(err, {
        component: 'DescriptionStep',
        action: 'generateSummary',
      });
      setError(t('common.error'));
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const handleRerollSummaries = async () => {
    setIsGeneratingSummary(true);
    setError('');
    setExpandedSummaryId(null);

    try {
      if (cachedSummaries.length >= 3) {
        // Use cached summaries
        const nextThree = cachedSummaries.slice(0, 3);
        setCachedSummaries(cachedSummaries.slice(3));
        setDisplayedSummaries(nextThree);
      } else {
        // Fetch more summaries
        const newSummaries = await fetchSummaryOptions();
        if (newSummaries.length > 0) {
          const nextThree = newSummaries.slice(0, 3);
          setCachedSummaries(newSummaries.slice(3));
          setDisplayedSummaries(nextThree);
        }
      }
    } catch (err) {
      captureException(err, {
        component: 'DescriptionStep',
        action: 'rerollSummaries',
      });
      setError(t('common.error'));
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const handleSelectSummary = async (selectedSummary: string) => {
    setSummary(selectedSummary);
    setShowSummarySuggestions(false);

    // In edit mode, mark as unsaved instead of auto-saving
    if (isEditMode) {
      setHasUnsavedChanges(true);
      return;
    }

    // Auto-save the selected summary
    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        updateBook: { book: Book; errors: string[] };
      }>(UPDATE_BOOK, {
        id: book.id,
        summary: selectedSummary,
      });

      if (data.updateBook.book) {
        onBookChange({ ...book, summary: selectedSummary });
      }
    } catch (err) {
      console.error('Failed to save summary:', err);
    }
  };

  const handleCloseSummarySuggestions = () => {
    setShowSummarySuggestions(false);
  };

  return (
    <motion.div
      ref={topRef}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {t('wizard.overviewStep')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mt-1">{t('wizard.overviewStepSubtitle')}</p>
      </div>

      {/* Profile Warning Banner */}
      {user && !user.profileComplete && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex items-start gap-3"
        >
          <svg
            className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div className="flex-1">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              {t('wizard.profileWarning')}
            </p>
            <Link
              to="/profile"
              className="text-sm font-medium text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 underline mt-1 inline-block"
            >
              {t('wizard.completeProfile')}
            </Link>
          </div>
        </motion.div>
      )}

      {/* Title Section */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
          {t('books.bookTitle')} <span className="text-red-500">*</span>
        </label>
        <AiEnhancedInput
          value={title}
          onChange={handleTitleChange}
          onBlur={handleTitleBlur}
          placeholder={t('books.bookTitle')}
          required
          onMenuAction={handleTitleAiAction}
          isLoading={isGeneratingTitle}
          hasContent={!!title?.trim()}
          showRephraseOptions={true}
          showChatOption={false}
          bookId={book.id}
        />
        {/* Clear Link */}
        {!showTitleSuggestions && title && (
          <div className="flex items-center gap-2">
            {title && (
              <button
                type="button"
                onClick={() => {
                  setTitle('');
                  if (isEditMode) {
                    setHasUnsavedChanges(true);
                    return;
                  }
                  // Save the empty title to the server
                  const client = getGraphQLClient();
                  client
                    .request(UPDATE_BOOK, {
                      id: book.id,
                      title: null,
                    })
                    .then((data: { updateBook: { book: Book } }) => {
                      if (data.updateBook.book) {
                        onBookChange({ ...book, title: null });
                      }
                    })
                    .catch((err: unknown) => {
                      console.error('Failed to clear title:', err);
                    });
                }}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                {t('common.clear')}
              </button>
            )}
          </div>
        )}

        {/* Title Suggestions Panel */}
        <AnimatePresence>
          {showTitleSuggestions && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 p-4 bg-gradient-to-br from-primary-50 to-primary-100/50 dark:from-primary-900/20 dark:to-primary-800/10 rounded-xl border border-primary-200 dark:border-primary-800"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                  {titlesBasedOnSummary
                    ? t('ai.titleSuggestionsWithSummary')
                    : t('ai.titleSuggestions')}
                </span>
                <button
                  type="button"
                  onClick={handleCloseTitleSuggestions}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-2 mb-3">
                {displayedTitles.map((suggestedTitle, index) => (
                  <motion.button
                    key={`${suggestedTitle}-${index}`}
                    type="button"
                    onClick={() => handleSelectTitle(suggestedTitle)}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="w-full text-left px-4 py-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500 hover:shadow-md transition-all duration-200 group"
                  >
                    <span className="text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {suggestedTitle}
                    </span>
                  </motion.button>
                ))}
              </div>

              <button
                type="button"
                onClick={handleRerollTitles}
                disabled={isGeneratingTitle}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 bg-white dark:bg-gray-800 border border-primary-300 dark:border-primary-700 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGeneratingTitle ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                )}
                {t('ai.rerollTitles')}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Subtitle Section - Only for non-fiction */}
      {book.bookType?.toLowerCase() === 'non_fiction' && (
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
            {t('books.subtitle')}
            <span className="text-gray-400 dark:text-gray-500 font-normal ml-2">
              ({t('common.optional')})
            </span>
          </label>
          <AiEnhancedInput
            type="text"
            value={subtitle}
            onChange={handleSubtitleChange}
            onBlur={handleSubtitleBlur}
            placeholder={t('books.subtitlePlaceholder')}
            onMenuAction={handleSubtitleAiAction}
            isLoading={isGeneratingSubtitle}
            hasContent={!!subtitle?.trim()}
            showRephraseOptions={true}
            showChatOption={false}
            bookId={book.id}
          />

          {/* Subtitle Suggestions Panel */}
          <AnimatePresence>
            {showSubtitleSuggestions && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3 p-4 bg-gradient-to-br from-primary-50 to-primary-100/50 dark:from-primary-900/20 dark:to-primary-800/10 rounded-xl border border-primary-200 dark:border-primary-800"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                    {t('ai.subtitleSuggestions')}
                  </span>
                  <button
                    type="button"
                    onClick={handleCloseSubtitleSuggestions}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="space-y-2 mb-3">
                  {displayedSubtitles.map((suggestedSubtitle, index) => (
                    <motion.button
                      key={`${suggestedSubtitle}-${index}`}
                      type="button"
                      onClick={() => handleSelectSubtitle(suggestedSubtitle)}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="w-full text-left px-4 py-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500 hover:shadow-md transition-all duration-200 group"
                    >
                      <span className="text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                        {suggestedSubtitle}
                      </span>
                    </motion.button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={handleRerollSubtitles}
                  disabled={isGeneratingSubtitle}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 bg-white dark:bg-gray-800 border border-primary-300 dark:border-primary-700 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGeneratingSubtitle ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  )}
                  {t('ai.rerollSubtitles')}
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <p className="text-xs text-gray-500 dark:text-gray-400">{t('books.subtitleHint')}</p>
        </div>
      )}

      {/* Summary Section */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
          {t('stories.summary')} <span className="text-red-500">*</span>
        </label>
        <AiEnhancedTextarea
          value={summary}
          onChange={(e) => handleSummaryChange(e.target.value)}
          onBlur={handleSummaryBlur}
          placeholder={t('generator.summaryPlaceholder')}
          required
          minRows={6}
          maxRows={24}
          onMenuAction={handleSummaryAiAction}
          isLoading={isGeneratingSummary}
          hasContent={!!summary?.trim()}
          showRephraseOptions={true}
          showChatOption={false}
          bookId={book.id}
        />
        {/* Clear Link */}
        {!showSummarySuggestions && summary && (
          <div className="flex items-center gap-2">
            {summary && (
              <button
                type="button"
                onClick={() => {
                  setSummary('');
                  if (isEditMode) {
                    setHasUnsavedChanges(true);
                  } else {
                    handleSummaryBlur();
                  }
                }}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                {t('common.clear')}
              </button>
            )}
          </div>
        )}

        {/* Summary Suggestions Panel */}
        <AnimatePresence>
          {showSummarySuggestions && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 p-4 bg-gradient-to-br from-primary-50 to-primary-100/50 dark:from-primary-900/20 dark:to-primary-800/10 rounded-xl border border-primary-200 dark:border-primary-800"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                  {t('ai.summarySuggestions')}
                </span>
                <button
                  type="button"
                  onClick={handleCloseSummarySuggestions}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-2 mb-3">
                {displayedSummaries.map((option, index) => {
                  const isExpanded = expandedSummaryId === option.id;
                  return (
                    <motion.div
                      key={option.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500 hover:shadow-md transition-all duration-200"
                    >
                      <button
                        type="button"
                        onClick={() => handleSelectSummary(option.summary)}
                        className="w-full text-left px-4 py-3 group"
                      >
                        <span
                          className={`text-sm text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors ${!isExpanded ? 'line-clamp-3' : ''}`}
                        >
                          {option.summary}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedSummaryId(isExpanded ? null : option.id);
                        }}
                        className="w-full px-4 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 border-t border-gray-100 dark:border-gray-700 transition-colors"
                      >
                        {isExpanded ? t('wizard.showLess') : t('wizard.showMore')}
                      </button>
                    </motion.div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={handleRerollSummaries}
                disabled={isGeneratingSummary}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 bg-white dark:bg-gray-800 border border-primary-300 dark:border-primary-700 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGeneratingSummary ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                )}
                {t('ai.rerollSummaries')}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {error && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-sm text-red-500"
        >
          {error}
        </motion.p>
      )}

      {/* Navigation */}
      <div className="flex gap-4 pt-4">
        <Button variant="secondary" onClick={onBack}>
          {t('wizard.back')}
        </Button>
        <Button className="flex-1" onClick={handleNext} disabled={!title.trim() || !summary.trim()}>
          {isEditMode ? t('wizard.next') : t('wizard.finish')}
        </Button>
      </div>
    </motion.div>
  );
}
